import {
	IconButton,
	Paper,
	SvgIcon,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	Tooltip,
} from "@mui/material";
import { useEffect } from "react";
import { Pagination, Sort } from "../interfaces/RequestBase";
import { TableTemplate } from "../interfaces/Table";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

interface IProps {
	rows: TableTemplate;
	data: any[];
	pagination: Pagination;
	setPage(value: number): void;
	setPageSize(value: number): void;
	sort?: Sort;
	setSort?(value: Sort): void;
	allCount: number;
	onExport: (callback: (data: any[]) => void) => void;
}

const CustomTable = (props: IProps) => {
	const downloadExcel = (dataRow: any[], fileName: string) => {
		const fileType =
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
		const fileExtension = ".xlsx";

		const wb = XLSX.utils.book_new();
		const newWs = XLSX.utils.aoa_to_sheet(dataRow);

		XLSX.utils.book_append_sheet(wb, newWs);
		const rawExcel = XLSX.write(wb, { bookType: "xlsx", type: "array" });

		const data = new Blob([rawExcel], { type: fileType });
		FileSaver.saveAs(data, fileName + fileExtension);
	};

	const downloadTable = (data: any[]) => {
		var result: any[] = [];
		const header = [];
		for (var h of props.rows.data) {
			if (!h.title || h.noExport) continue;
			header.push(h.title);
		}

		result.push(header);

		for (var r of data || []) {
			var row: any = [];
			for (var c of props.rows.data) {
				if (!c.title || c.noExport) continue;
				if (c.renderExport) row.push(c.renderExport(r));
				else if (c.key) row.push(r[c.key]);
				else if (c.render) row.push(c.render(r));
			}
			result.push(row);
		}

		const url = window.location.href.split("/");

		var filename = url[url.length - 1];
		if (parseInt(filename) > -1) {
			filename = url[url.length - 2];
		}

		downloadExcel(result, filename);
	};

	const onChangeSort = (property: string) => {
		const isAscending =
			props.sort?.order_by === property && props.sort?.order_dir === "asc";
		if (props.sort?.order_by === property && !isAscending) {
			props.setSort?.({});
		} else {
			props.setSort?.({
				order_by: property,
				order_dir: isAscending ? "desc" : "asc",
			});
		}
	};

	const onExport = () => {
		props.onExport?.(downloadTable);
	};

	useEffect(() => {
		if (
			props.allCount > 0 &&
			props.pagination.page * props.pagination.size > props.allCount
		) {
			props.setPage(~~(props.allCount / props.pagination.size));
		}
	}, [props.pagination, props.allCount]);

	return (
		<div className="w-full flex flex-col">
			<div className="flex gap-4">
				<Tooltip title="Скачать XLSX">
					<IconButton onClick={onExport}>
						<SvgIcon color="primary" viewBox="1 0 13 16" sx={{ fontSize: 32 }}>
							<path
								fillRule="evenodd"
								d="M14 4.5V11h-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5L14 4.5ZM7.86 14.841a1.13 1.13 0 0 0 .401.823c.13.108.29.192.479.252.19.061.411.091.665.091.338 0 .624-.053.858-.158.237-.105.416-.252.54-.44a1.17 1.17 0 0 0 .187-.656c0-.224-.045-.41-.135-.56a1.002 1.002 0 0 0-.375-.357 2.028 2.028 0 0 0-.565-.21l-.621-.144a.97.97 0 0 1-.405-.176.37.37 0 0 1-.143-.299c0-.156.061-.284.184-.384.125-.101.296-.152.513-.152.143 0 .266.023.37.068a.624.624 0 0 1 .245.181.56.56 0 0 1 .12.258h.75a1.093 1.093 0 0 0-.199-.566 1.21 1.21 0 0 0-.5-.41 1.813 1.813 0 0 0-.78-.152c-.293 0-.552.05-.777.15-.224.099-.4.24-.527.421-.127.182-.19.395-.19.639 0 .201.04.376.123.524.082.149.199.27.351.367.153.095.332.167.54.213l.618.144c.207.049.36.113.462.193a.387.387 0 0 1 .153.326.512.512 0 0 1-.085.29.558.558 0 0 1-.255.193c-.111.047-.25.07-.413.07-.117 0-.224-.013-.32-.04a.837.837 0 0 1-.249-.115.578.578 0 0 1-.255-.384h-.764Zm-3.726-2.909h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415H1.5l1.24-2.016-1.228-1.983h.931l.832 1.438h.036l.823-1.438Zm1.923 3.325h1.697v.674H5.266v-3.999h.791v3.325Zm7.636-3.325h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415h-.861l1.24-2.016-1.228-1.983h.931l.832 1.438h.036l.823-1.438Z"
							/>
						</SvgIcon>
					</IconButton>
				</Tooltip>
			</div>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>№</TableCell>
							{props.rows.data.map((t, i) => (
								<TableCell key={`h-${i}`}>
									{t.sortKey ? (
										<TableSortLabel
											active={props.sort?.order_by === t.sortKey}
											direction={
												props.sort?.order_by === t.sortKey
													? props.sort?.order_dir
													: "desc"
											}
											onClick={(event: any) => onChangeSort(t.sortKey || "")}
										>
											{t.title}
										</TableSortLabel>
									) : (
										t.title
									)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{props.data.map((row, i) => (
							<TableRow
								key={`row-${i}`}
								style={props.rows.rowStyle ? props.rows.rowStyle(row) : {}}
							>
								<TableCell>
									{props.allCount - (props.pagination.page * props.pagination.size + i)}
								</TableCell>
								{props.rows.data.map((c, i) => {
									var d = "";
									if (c.render) d = c.render(row);
									if (c.key) d = row[c.key];
									return <TableCell key={`cell-${i}`}>{d}</TableCell>;
								})}
							</TableRow>
						))}
					</TableBody>
				</Table>
				<div className="w-full flex justify-end float-left">
					<TablePagination
						rowsPerPageOptions={[5, 10, 25, 50]}
						component="div"
						count={props.allCount || 0}
						rowsPerPage={props.pagination.size}
						page={props.pagination.page}
						onPageChange={(e, n) => props.setPage(n)}
						onRowsPerPageChange={(e) => {
							props.setPageSize(parseInt(e.target.value));
						}}
					/>
				</div>
			</TableContainer>
		</div>
	);
};

export default CustomTable;
